<script setup lang="ts">
import { stringToFaDateTime } from '@payjib/shared'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { getOrderAction, updateOrderStatusAction } from '@/api/functions/orders/orders.action'
import { useMutationApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'

const route = useRoute()
const toast = useToast()
const queryClient = useQueryClient()

const orderId = computed(() => route.params.id as string)

const { data: orderResponse, isLoading } = useQuery({
  queryKey: ['order', orderId],
  queryFn: () => getOrderAction(orderId.value).then((res) => res.data),
  enabled: computed(() => !!orderId.value),
})

const order = computed(() => {
  const data = orderResponse.value

  if (!data) return null

  const item = data.items?.[0]
  const servicePlan = item?.service_plan

  return {
    ...data,

    user: {
      id: data.user?.id ?? '—',
      code: data.user?.code ?? '—',
      fullName: data.user?.full_name ?? '—',
      phone: data.user?.mobile ?? '—',
      email: data.user?.email ?? '—',
    },

    service: data.custom_service_request != null ? 'سرویس سفارشی' : (servicePlan?.service?.name ?? '—'),
    plan: servicePlan?.name ?? 'سفارشی',
    type: data.custom_service_request != null ? 'سفارشی' : 'عادی',

    quantity: item?.quantity ?? 0,
    unitPrice: Number(item?.unit_price ?? 0),

    totalRial: Number(data.final_amount ?? 0),
    subtotalAmount: Number(data.subtotal_amount ?? 0),
    feeAmount: Number(data.fee_amount ?? 0),
    totalAmount: Number(data.total_amount ?? 0),
    discountAmount: Number(data.discount_amount ?? 0),
    shippingAmount: Number(data.shipping_amount ?? 0),
    taxAmount: Number(data.tax_amount ?? 0),

    customServiceRequest: data.custom_service_request ?? null,

    payment: {
      status: data.payment_status ?? '—',
      method: '—',
      transactionId: '—',
      paidAt: '—',
    },

    subscription: {
      duration: servicePlan?.duration_days ? `${servicePlan.duration_days} روز` : '—',
      startDate: '—',
      endDate: '—',
    },

    // date: data.created_at
    //   ? new Intl.DateTimeFormat('fa-IR', {
    //       year: 'numeric',
    //       month: '2-digit',
    //       day: '2-digit',
    //     }).format(new Date(data.created_at))
    //   : '—',

    // time: data.created_at
    //   ? new Intl.DateTimeFormat('fa-IR', {
    //       hour: '2-digit',
    //       minute: '2-digit',
    //     }).format(new Date(data.created_at))
    //   : '—',
    createdAt: data.created_at ? stringToFaDateTime(data.created_at) : '—',
  }
})

const orderStatusMeta: Record<
  string,
  {
    label: string
    class: string
  }
> = {
  awaiting_payment: {
    label: 'در انتظار پرداخت',
    class: 'bg-[#fff8e7] text-[#ffba0e]',
  },
  completing_payment: {
    label: 'در حال تکمیل پرداخت',
    class: 'bg-[#fff8e7] text-[#ffba0e]',
  },
  pending: {
    label: 'در انتظار',
    class: 'bg-[#fff8e7] text-[#ffba0e]',
  },
  paid: {
    label: 'پرداخت شده',
    class: 'bg-[#e6fff0] text-[#2ed06e]',
  },

  reviewing: {
    label: 'در حال بررسی',
    class: 'bg-[#d5ddea] text-[#2d5396]',
  },

  action_required: {
    label: 'نیاز به اقدام',
    class: 'bg-[#fff8e7] text-[#ffba0e]',
  },

  processing: {
    label: 'در حال پردازش',
    class: 'bg-[#d5ddea] text-[#2d5396]',
  },

  done: {
    label: 'انجام شده',
    class: 'bg-[#e6fff0] text-[#2ed06e]',
  },

  failed: {
    label: 'ناموفق',
    class: 'bg-[#fde7ef] text-[#ed145b]',
  },

  rejected: {
    label: 'رد شده',
    class: 'bg-[#fde7ef] text-[#ed145b]',
  },

  cancelled: {
    label: 'لغو شده',
    class: 'bg-[#fde7ef] text-[#ed145b]',
  },
}

type OrderStatus = keyof typeof orderStatusMeta

const orderStatusOptions = Object.entries(orderStatusMeta).map(([value, item]) => ({
  label: item.label,
  value,
}))

const currentStatus = computed(() => {
  if (!order.value) {
    return {
      label: '—',
      class: 'bg-[#d5ddea] text-[#2d5396]',
    }
  }

  return (
    orderStatusMeta[order.value.status] ?? {
      label: order.value.status ?? '—',
      class: 'bg-[#d5ddea] text-[#2d5396]',
    }
  )
})

const orderTypeBadge = computed(() => {
  if (!order.value) {
    return { label: '—', class: 'bg-[#d5ddea] text-[#2d5396]' }
  }

  if (order.value.type === 'سفارشی') {
    return { label: 'سفارشی', class: 'bg-[#F2E3F4] text-[#CB30E0]' }
  }

  return { label: 'عادی', class: 'bg-[#E8F5FF] text-[#0E59FA]' }
})

const customRequestStatus = computed(() => {
  const status = order.value?.customServiceRequest?.status

  if (!status) {
    return { label: '—', class: 'bg-[#d5ddea] text-[#2d5396]' }
  }

  return (
    orderStatusMeta[status] ?? {
      label: status,
      class: 'bg-[#d5ddea] text-[#2d5396]',
    }
  )
})

const selectedStatus = ref<OrderStatus>('paid')

watch(
  orderResponse,
  (value) => {
    if (value?.status) {
      selectedStatus.value = value.status as OrderStatus
    }
  },
  { immediate: true }
)

const updateOrderStatusMutation = useMutationApi({
  mutationFn: ({ id, status }: { id: string; status: OrderStatus }) => updateOrderStatusAction(id, { status }),

  onSuccess: (response) => {
    const newStatus = response?.data?.status as OrderStatus

    selectedStatus.value = newStatus

    queryClient.invalidateQueries({ queryKey: ['order', orderId] })
    queryClient.invalidateQueries({ queryKey: ['orders'] })

    toast.success('وضعیت سفارش با موفقیت تغییر کرد')
  },

  onError: (error: Error) => {
    if (order.value?.status) {
      selectedStatus.value = order.value.status as OrderStatus
    }

    toast.error(getErrorMessage(error), 'خطا')
  },
})

const handleStatusChange = (status: OrderStatus) => {
  if (!status || !order.value) return

  if (status === order.value.status) {
    return
  }

  updateOrderStatusMutation.mutate({
    id: orderId.value,
    status,
  })
}

// Order history is disabled for now (see template "TODO: disabled in 2.1.4").
</script>

<template>
  <div>

  <!-- Loading -->
  <div v-if="isLoading" class="mb-9">
    <div class="w-full flex flex-col p-5 lg:pb-8 bg-white rounded-xl gap-4 mb-4">
      <Skeleton class="h-8 w-40 rounded-lg" />
      <Skeleton class="h-10 w-48 rounded-lg mt-4 lg:mt-0" />
    </div>

    <div class="w-full flex flex-col p-5 lg:p-8 bg-white  rounded-xl gap-4">
      <Skeleton class="h-4 w-2/3 rounded-md" />
      <Skeleton class="h-4 w-1/2 rounded-md" />
      <Skeleton class="h-4 w-3/4 rounded-md" />
      <Skeleton class="h-4 w-1/3 rounded-md" />
    </div>
  </div>

  <!-- Order -->
  <div v-else-if="order" class="mb-9">
    <router-link to="/orders"
      class="w-fit mb-4 gap-1 flex items-center h-10 rounded-lg text-[#2d5396] px-5 hover:bg-[#D5E2F1] text-[13px]">
      <img src="/svg/back-icon.svg" class="me-1" width="22" />

      <span>
        بازگشت
      </span>
    </router-link>
    <!-- Header -->
    <div class="flex flex-col lg:flex-row lg:items-center justify-between mb-6">
      <div class="w-full lg:w-auto flex flex-row justify-between items-center gap-2">
        <div class="text-[#1b325a] text-[22px]">
          <span>
            سفارش شماره

            <span class="font-fa ms-1">
              {{ order.order_number }}
            </span>
          </span>
        </div>

        <div class="w-auto flex flex-row items-center justify-between lg:justify-start">
          <div class="w-fit flex items-center justify-center text-[13px] font-medium h-10 rounded-[10px] px-6 ms-6 me-2"
            :class="currentStatus.class">
            {{ currentStatus.label }}
          </div>
        </div>
      </div>

      <div class="w-full lg:w-1/3 mt-4 lg:mt-0">
        <div class="flex items-center gap-3">
          <span class="text-[13px] font-medium text-[#2d5396] whitespace-nowrap">
            تغییر وضعیت:
          </span>

          <Select variant="checkbox" v-model="selectedStatus" class="h-10!"
            :disabled="updateOrderStatusMutation.isPending?.value" :options="orderStatusOptions"
            @update:modelValue="handleStatusChange" />
        </div>
      </div>
    </div>

    <!-- Order Information -->
    <div class="w-full flex flex-col p-5 lg:p-8 bg-white border border-[#d8d8d8] rounded-xl">
      <div class="flex flex-col lg:flex-row justify-between gap-6 lg:gap-[168.773px]">
        <div class="w-full lg:w-1/2 flex flex-col gap-3">
          <div class="w-full flex items-center justify-between text-sm font-medium">
            <span class="text-[#2d5396]">
              تاریخ ثبت:
            </span>

            <span dir="ltr" class="font-fa text-[#1b325a]">
                {{ order.createdAt }}
            </span>
          </div>

          <div class="w-full flex items-center justify-between text-sm font-medium">
            <span class="text-[#2d5396]">
              کاربر:
            </span>

            <router-link
              v-if="order.user.id !== '—'"
              :to="`/users/${order.user.id}`"
              class="text-[#2d5396] hover:underline transition-colors"
            >
              {{ order.user.fullName }}

              <span class="font-fa">
                ({{ order.user.code }})
              </span>
            </router-link>

            <span v-else class="text-[#1b325a]">
              {{ order.user.fullName }}
            </span>
          </div>

          <div class="w-full flex items-center justify-between text-sm font-medium">
            <span class="text-[#2d5396]">
              شماره تماس:
            </span>

            <span dir="ltr" class="font-fa text-[#1b325a]">
              {{ order.user.phone }}
            </span>
          </div>

          <div class="w-full flex items-center justify-between text-sm font-medium">
            <span class="text-[#2d5396]">
              ایمیل:
            </span>

            <span dir="ltr" class="text-[#1b325a]">
              {{ order.user.email }}
            </span>
          </div>
        </div>

        <div class="w-full lg:w-1/2 flex flex-col gap-3">
          <div class="w-full flex items-center justify-between text-sm font-medium">
            <span class="text-[#2d5396]">
              سرویس:
            </span>

            <span class="text-[#1b325a]">
              {{ order.service }}
            </span>
          </div>

          <div class="w-full flex items-center justify-between text-sm font-medium">
            <span class="text-[#2d5396]">
              پلن:
            </span>

            <span class="text-[#1b325a]">
              {{ order.plan }}
            </span>
          </div>

          <div class="w-full flex items-center justify-between text-sm font-medium">
            <span class="text-[#2d5396]">
              نوع سفارش:
            </span>

            <span class="w-fit px-3 py-1 rounded-lg text-[12px] font-medium" :class="orderTypeBadge.class">
              {{ orderTypeBadge.label }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Service Details -->
    <div class="flex flex-col lg:flex-row lg:items-center justify-between mb-4 mt-8 text-[#1b325a] text-[22px]">
      جزئیات سرویس
    </div>

    <div class="w-full flex flex-col p-5 lg:p-8 bg-white border border-[#d8d8d8] rounded-xl">
      <div class="flex flex-col lg:flex-row justify-between gap-6 lg:gap-[168.773px]">
        <div class="w-full lg:w-1/2 flex flex-col gap-3">
          <div class="w-full flex items-center justify-between text-sm font-medium">
            <span class="text-[#2d5396]">
              سرویس:
            </span>

            <span class="text-[#1b325a]">
              {{ order.service }}
            </span>
          </div>

          <div class="w-full flex items-center justify-between text-sm font-medium">
            <span class="text-[#2d5396]">
              پلن:
            </span>

            <span class="text-[#1b325a]">
              {{ order.plan }}
            </span>
          </div>
        </div>

        <div class="w-full lg:w-1/2 flex flex-col gap-3">
          <div class="w-full flex items-center justify-between text-sm font-medium">
            <span class="text-[#2d5396]">
              مدت اشتراک:
            </span>

            <span class="text-[#1b325a]">
              {{ order.subscription.duration }}
            </span>
          </div>

          <div class="w-full flex items-center justify-between text-sm font-medium">
            <span class="text-[#2d5396]">
              تاریخ شروع:
            </span>

            <span dir="ltr" class="font-fa text-[#1b325a]">
              {{ order.subscription.startDate }}
            </span>
          </div>

          <div class="w-full flex items-center justify-between text-sm font-medium">
            <span class="text-[#2d5396]">
              تاریخ پایان:
            </span>

            <span dir="ltr" class="font-fa text-[#1b325a]">
              {{ order.subscription.endDate }}
            </span>
          </div>
        </div>
      </div>
    </div>


    <!-- Custom Service Request -->
    <template v-if="order.customServiceRequest">
      <div class="flex flex-col lg:flex-row lg:items-center justify-between mb-4 mt-8 text-[#1b325a] text-[22px]">
        اطلاعات سفارش سفارشی
      </div>

      <div class="w-full flex flex-col p-5 lg:p-8 bg-white border border-[#d8d8d8] rounded-xl">
        <div class="flex flex-col lg:flex-row justify-between gap-6">
          <div class="w-full lg:w-1/2 flex flex-col gap-3">
            <div class="w-full flex items-center justify-between text-sm font-medium">
              <span class="text-[#2d5396]">نام سرویس:</span>
              <span class="text-[#1b325a]">{{ order.customServiceRequest.service_name }}</span>
            </div>

            <div class="w-full flex items-center justify-between text-sm font-medium">
              <span class="text-[#2d5396]">مبلغ:</span>
              <span class="font-fa text-[#1b325a]">{{ order.customServiceRequest.amount }}</span>
            </div>

            <div class="w-full flex items-center justify-between text-sm font-medium">
              <span class="text-[#2d5396]">ارز:</span>
              <span class="text-[#1b325a]">
                {{ order.customServiceRequest.currency?.title ?? '—' }}
                ({{ order.customServiceRequest.currency?.iso_code ?? '—' }})
              </span>
            </div>

            <div class="w-full flex items-center justify-between text-sm font-medium">
              <span class="text-[#2d5396]">کشور:</span>
              <span class="text-[#1b325a]">{{ order.customServiceRequest.country?.title ?? '—' }}</span>
            </div>
          </div>

          <div class="w-full lg:w-1/2 flex flex-col gap-3">
            <div class="w-full flex items-center justify-between text-sm font-medium">
              <span class="text-[#2d5396]">وضعیت:</span>
              <span class="w-fit px-3 py-1 rounded-lg text-[12px] font-medium" :class="customRequestStatus.class">
                {{ customRequestStatus.label }}
              </span>
            </div>

            <div class="w-full flex items-center justify-between text-sm font-medium">
              <span class="text-[#2d5396]">لینک سرویس:</span>
              <a v-if="order.customServiceRequest.service_url" :href="order.customServiceRequest.service_url"
                target="_blank" rel="noopener noreferrer" dir="ltr"
                class="font-fa text-[#2d5396] underline truncate max-w-[220px] transition-colors hover:text-[#1b325a]">
                {{ order.customServiceRequest.service_url }}
              </a>
              <span v-else class="text-[#1b325a]">—</span>
            </div>

            <div class="w-full flex items-center justify-between text-sm font-medium">
              <span class="text-[#2d5396]">لینک پرداخت:</span>
              <a v-if="order.customServiceRequest.payment_url" :href="order.customServiceRequest.payment_url"
                target="_blank" rel="noopener noreferrer" dir="ltr"
                class="font-fa text-[#2d5396] underline truncate max-w-[220px] transition-colors hover:text-[#1b325a]">
                {{ order.customServiceRequest.payment_url }}
              </a>
              <span v-else class="text-[#1b325a]">—</span>
            </div>
          </div>
        </div>

        <div v-if="order.customServiceRequest.description"
          class="mt-6 flex flex-col gap-2 border-t border-[#eaeef5] pt-4">
          <span class="text-[#2d5396] text-sm font-medium">
            توضیحات:
          </span>

          <span class="text-[#1b325a] text-sm leading-7">
            {{ order.customServiceRequest.description }}
          </span>
        </div>
      </div>
    </template>


    <!-- Payment Information -->
    <div class="flex flex-col lg:flex-row lg:items-center justify-between mb-4 mt-8 text-[#1b325a] text-[22px]">
      اطلاعات پرداخت

      <span v-if="order.customServiceRequest"
        class="px-3 py-1 rounded-lg bg-[#eaeef5] text-[#8996ac] text-[12px] font-medium">
        غیر فعال
      </span>
    </div>

    <div class="w-full flex flex-col p-5 lg:p-8 bg-white border border-[#d8d8d8] rounded-xl transition-opacity"
      :class="order.customServiceRequest ? 'opacity-50 pointer-events-none select-none' : ''">
      <div class="flex flex-col lg:flex-row justify-between gap-6 lg:gap-[168.773px]">
        <div class="w-full lg:w-1/2 flex flex-col gap-3">
          <div class="w-full flex items-center justify-between text-sm font-medium">
            <span class="text-[#2d5396]">
              جمع اقلام:
            </span>

            <span class="font-fa text-[#1b325a]">
              {{ order.subtotalAmount.toLocaleString() }}
              تومان
            </span>
          </div>

          <div class="w-full flex items-center justify-between text-sm font-medium">
            <span class="text-[#2d5396]">
              کارمزد:
            </span>

            <span class="font-fa text-[#1b325a]">
              {{ order.feeAmount.toLocaleString() }}
              تومان
            </span>
          </div>

          <div class="w-full flex items-center justify-between text-sm font-medium">
            <span class="text-[#2d5396]">
              تخفیف:
            </span>

            <span class="font-fa text-[#1b325a]">
              {{ order.discountAmount.toLocaleString() }}
              تومان
            </span>
          </div>

          <div class="w-full flex items-center justify-between text-sm font-medium">
            <span class="text-[#2d5396]">
              مبلغ کل:
            </span>

            <span class="font-fa text-[#1b325a]">
              {{ order.totalAmount.toLocaleString() }}
              تومان
            </span>
          </div>

          <div class="w-full flex items-center justify-between text-sm font-medium">
            <span class="text-[#2d5396]">
              مالیات:
            </span>

            <span class="font-fa text-[#1b325a]">
              {{ order.taxAmount.toLocaleString() }}
              تومان
            </span>
          </div>

          <div class="w-full flex items-center justify-between text-sm font-bold border-t border-[#eaeef5] pt-3">
            <span class="text-[#2d5396]">
              مبلغ نهایی:
            </span>

            <span class="font-fa text-[#2d5396]">
              {{ order.totalRial.toLocaleString() }}
              تومان
            </span>
          </div>
        </div>

        <div class="w-full lg:w-1/2 flex flex-col gap-3">
          <div class="w-full flex items-center justify-between text-sm font-medium">
            <span class="text-[#2d5396]">
              وضعیت پرداخت:
            </span>

            <span :class="order.payment.status === 'paid'
              ? 'text-[#2ed06e]'
              : 'text-[#ffba0e]'
              ">
              {{
                order.payment.status === 'paid'
                  ? 'پرداخت شده'
                  : 'در انتظار پرداخت'
              }}
            </span>
          </div>

          <div class="w-full flex items-center justify-between text-sm font-medium">
            <span class="text-[#2d5396]">
              روش پرداخت:
            </span>

            <span class="text-[#1b325a]">
              {{ order.payment.method }}
            </span>
          </div>

          <div class="w-full flex items-center justify-between text-sm font-medium">
            <span class="text-[#2d5396]">
              شماره تراکنش:
            </span>

            <span class="font-fa text-[#1b325a]">
              {{ order.payment.transactionId }}
            </span>
          </div>

          <div class="w-full flex items-center justify-between text-sm font-medium">
            <span class="text-[#2d5396]">
              تاریخ پرداخت:
            </span>

            <span dir="ltr" class="font-fa text-[#1b325a]">
              {{ order.payment.paidAt }}
            </span>
          </div>
        </div>
      </div>
    </div>
    <!-- TODO: disabled in 2.1.4 -->
    <!-- <div class="flex flex-col lg:flex-row lg:items-center justify-between mb-4 mt-8 text-[#1b325a] text-[22px]">
      تاریخچه سفارش
    </div>

    <div class="w-full bg-white border border-[#d8d8d8] rounded-xl p-5 lg:p-8">
      <Table :columns="historyColumns" :rows="orderHistory" />
    </div> -->
  </div>

  <!-- Not Found -->
  <div v-else class="w-full py-10 text-center text-[#2d5396]">
    سفارش پیدا نشد.
  </div>
  </div>
</template>
